import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { apiV2 } from "api/firebase";
import { IdleManager } from "tools/idle";

/**
 * Keyed by `exhibitorPairId` — a JET magic link belongs to a company-stand
 * pair, not to the show. Link availability also stands in for "integration is
 * enabled": the endpoint 404s while it is disabled and answers
 * `{link: null, isSyncing: true}` until the pair has been pushed once.
 */
type State = {
  items: Record<string, string | null>;
  isLoading: boolean;
};

type Action = {
  fetch: (exhibitorPairId: string, showId: string) => Promise<void>;
  update: (exhibitorPairId: string, showId: string) => Promise<void>;
  autoCatch: (exhibitorPairId: string, showId: string) => Promise<void>;
  getByPairId: (exhibitorPairId: string) => string | null;
};

export const useJetSsoLinkStore = create(
  devtools<State & Action>((set, get) => ({
    isLoading: false,
    items: {},

    getByPairId: (exhibitorPairId: string): string | null => {
      const { items } = get();

      if (!items?.[exhibitorPairId]) {
        return null;
      }

      return items[exhibitorPairId];
    },

    autoCatch: async (exhibitorPairId: string, showId: string) => {
      const { update } = get();

      IdleManager.getInstance().addSubscriber(
        `jet-sso-${exhibitorPairId}`,
        async () => {
          await update(exhibitorPairId, showId);
        }
      );
    },

    update: async (exhibitorPairId: string, showId: string) => {
      const { items } = get();

      try {
        const { link } = await apiV2.jet.getSsoLink({
          showId,
          exhibitorPairId,
        });

        set(() => ({
          items: {
            ...items,

            [exhibitorPairId]: link,
          },
        }));
      } catch (e) {
        // 404 = integration disabled for this show. Cache the miss so the
        // entry points stay hidden without re-asking on every render.
        set(() => ({
          items: {
            ...items,

            [exhibitorPairId]: null,
          },
        }));
      }
    },

    fetch: async (exhibitorPairId: string, showId: string, force = false) => {
      const { isLoading, items, update } = get();

      if (!force) {
        if (items[exhibitorPairId] || isLoading) {
          return;
        }
      }

      set(() => ({ isLoading: true }));

      try {
        await update(exhibitorPairId, showId);
      } catch (e) {
        console.error("jet sso link store:", e);
      } finally {
        set(() => ({ isLoading: false }));
      }
    },
  }))
);
